import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { UdRegisterService } from '../../services/udRegister.service';
import { PERMISSIONS } from '../../constants/permissions';
import { IUdRegister, UdStatus } from '../../interfaces/commercial.interface';
import { formatDate } from '../../utils/dateUtils';
import { enumLabel } from '../../utils/garmentsLabels';

const STATUS_COLOR: Record<UdStatus, 'default' | 'info' | 'success' | 'warning'> = {
    DRAFT: 'default', SUBMITTED: 'info', APPROVED: 'success', CLOSED: 'warning',
};

const DUE_COLOR: Record<string, 'default' | 'warning' | 'error'> = {
    OK: 'default', DUE_SOON: 'warning', EXPIRED: 'error',
};

const UdRegisterList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.UD_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<IUdRegister[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<UdStatus | ''>('');
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    const load = async () => {
        try {
            setLoading(true);
            const res = await UdRegisterService.list(institutionId, page, size, status);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load UD registers');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [page, size, status]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box sx={{ p: 3 }} data-help-page="commercial.udList.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">UD Register</Typography>
                {canManage && (
                    <Button data-help="commercial.udList.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/commercial/ud-register/new')}>
                        New UD
                    </Button>
                )}
            </Box>

            <Box data-help-section="commercial.udList.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField
                    data-help="commercial.udList.statusFilter" select label="Status" size="small" value={status}
                    onChange={(e) => { setStatus(e.target.value as UdStatus | ''); setPage(0); }}
                    sx={{ flex: '1 1 200px', maxWidth: 320 }}
                >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="DRAFT">Draft</MenuItem>
                    <MenuItem value="SUBMITTED">Submitted</MenuItem>
                    <MenuItem value="APPROVED">Approved</MenuItem>
                    <MenuItem value="CLOSED">Closed</MenuItem>
                </TextField>
            </Box>

            {loading ? <CircularProgress /> : (
                <TableContainer component={Paper} data-help-section="commercial.udList.table">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="commercial.udList.col.number">UD Number</TableCell>
                                <TableCell data-help="commercial.udList.col.date">UD Date</TableCell>
                                <TableCell data-help="commercial.udList.col.authority">Issuing Authority</TableCell>
                                <TableCell data-help="commercial.udList.col.validTill">Valid Till</TableCell>
                                <TableCell data-help="commercial.udList.col.status">Status</TableCell>
                                <TableCell data-help="commercial.udList.col.due">Due</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(r => (
                                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/commercial/ud-register/${r.id}`)}>
                                    <TableCell>{r.udNumber}</TableCell>
                                    <TableCell>{formatDate(r.udDate)}</TableCell>
                                    <TableCell>{r.issuingAuthority}</TableCell>
                                    <TableCell>{r.validTillDate ? formatDate(r.validTillDate) : '—'}</TableCell>
                                    <TableCell><Chip data-help="commercial.udList.status" size="small" label={enumLabel(r.status)} color={STATUS_COLOR[r.status]} /></TableCell>
                                    <TableCell>
                                        {r.dueStatus !== 'OK' && (
                                            <Chip data-help="commercial.udList.due" size="small" label={r.dueReason ?? enumLabel(r.dueStatus)} color={DUE_COLOR[r.dueStatus]} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rows.length === 0 && (
                                <TableRow><TableCell colSpan={6} align="center">No UD registers found.</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
                    />
                </TableContainer>
            )}
        </Box>
    );
};

export default UdRegisterList;
